import { useState, useCallback, useRef, useEffect } from 'react';
import { mutate as swrMutate } from 'swr';
import { useApolloClient } from '@apollo/client';
import { GetUserInfoQuery } from 'utils/queries';
import { v4 as uuidv4 } from 'uuid';
import {
  useAiChatMutation,
  Role,
  type Conversation,
  useAiFindOrCreateLatestConversationMutation,
} from '@updater/consumer-graph';
import { useAiChatUploader } from '../utils/aiChatUploader';

const ERROR_MESSAGES = {
  unsupportedFile:
    'The file type you uploaded is not supported. Please try again with a PDF.',
  general: 'Sorry, something went wrong. Please try again later.',
};

// Types
export interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  attachments?: Array<{ name: string }>;
}

export interface ChatInterfaceRef {
  addMessage: (
    message: Omit<Message, 'id' | 'timestamp'>,
    shouldAnimate?: boolean
  ) => void;
}

// Configurable TTL for conversations (in days)
const CONVERSATION_TTL_DAYS = 7;
const CONVERSATION_TTL_MS = CONVERSATION_TTL_DAYS * 24 * 60 * 60 * 1000;

export const useChatMessaging = () => {
  console.log('[aiChat] useChatMessaging initialized');
  const apolloClient = useApolloClient();
  // Start a new conversation by resetting state and forcing a new conversation on next send
  const startNewConversation = useCallback(async () => {
    setIsStartingNewConversation(true);
    try {
      const result = await findOrCreateRef.current({
        fetchPolicy: 'no-cache',
        variables: { newConversation: true },
      });
      const conv: Conversation | null =
        (result.data
          ?.aiFindOrCreateLatestConversation as unknown as Conversation | null) ??
        null;
      setConversation(conv);
      setMessages([]);
      setPendingUserMessage(null);
      setPendingAssistantMessage(null);
      setIsFirstMessage(true);
      setChatSessionKey(`${Date.now()}`);
      setAnimateLastAssistant(false);
      setHasInitializedFromConversation(false);
      // Refetch progress data after starting a new conversation
      void Promise.allSettled([
        swrMutate('/v2/items'),
        swrMutate('/v2/item_definitions'),
        apolloClient.refetchQueries({ include: [GetUserInfoQuery] }),
      ]);
    } catch (err) {
      setConversation(null);
      setMessages([]);
      setPendingUserMessage(null);
      setPendingAssistantMessage(null);
      setIsFirstMessage(true);
      setChatSessionKey(`${Date.now()}`);
      setAnimateLastAssistant(false);
      setHasInitializedFromConversation(false);
      // Optionally log or handle error
      console.error('Failed to start new conversation', err);
    } finally {
      setIsStartingNewConversation(false);
    }
  }, [apolloClient]);
  // Core state
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStartingNewConversation, setIsStartingNewConversation] =
    useState(false);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(
    null
  );
  const [pendingAssistantMessage, setPendingAssistantMessage] = useState<
    string | null
  >(null);
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [chatSessionKey, setChatSessionKey] = useState<string>(
    () => `${Date.now()}`
  );
  const [animateLastAssistant, setAnimateLastAssistant] = useState(false);
  const [hasInitializedFromConversation, setHasInitializedFromConversation] =
    useState(false);

  // API hooks
  const [aiChatMutation] = useAiChatMutation();
  const { uploadAiChatWithFiles } = useAiChatUploader();
  const [findOrCreateLatestConversation] =
    useAiFindOrCreateLatestConversationMutation();
  const findOrCreateRef = useRef(findOrCreateLatestConversation);
  findOrCreateRef.current = findOrCreateLatestConversation;

  // Initialize conversation on mount
  useEffect(() => {
    let isActive = true;
    const init = async () => {
      setIsLoading(true);
      try {
        const result = await findOrCreateRef.current({
          fetchPolicy: 'no-cache',
          variables: { newConversation: false },
        });
        const conv: Conversation | null =
          (result.data
            ?.aiFindOrCreateLatestConversation as unknown as Conversation | null) ??
          null;

        if (conv?.updatedAt) {
          const updatedAtMs = new Date(conv.updatedAt).getTime();
          const isStale =
            Number.isFinite(updatedAtMs) &&
            Date.now() - updatedAtMs > CONVERSATION_TTL_MS;
          if (isStale) {
            // Stale: start a new chat on first user send
            if (isActive) {
              setConversation(null);
              setIsFirstMessage(true);
            }
          } else if (isActive) {
            setConversation(conv);
            setIsFirstMessage(false);
          }
        } else if (isActive && conv) {
          // No updatedAt means treat as fresh
          setConversation(conv);
          setIsFirstMessage(false);
        }
      } catch (err) {
        // Fail silently; the chat can still operate in streaming mode on first user send
        console.error('Failed to initialize conversation', err);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };
    void init();
    return () => {
      isActive = false;
    };
  }, []);

  // Initialize messages from conversation
  useEffect(() => {
    if (
      conversation?.messages &&
      conversation.messages.length > 0 &&
      !hasInitializedFromConversation
    ) {
      const serverMessages: Message[] = conversation.messages.map(
        (msg: {
          id: string;
          content: string;
          role: string;
          createdAt: string;
        }) => ({
          id: msg.id,
          text: msg.content,
          isUser: msg.role === Role.USER,
          timestamp: new Date(msg.createdAt),
        })
      );
      setMessages((prev) => {
        if (prev.length === 0) {
          return serverMessages;
        }
        // If we already have messages, merge server messages that aren't already present
        const existingIds = new Set(prev.map((m) => m.id));
        const newServerMessages = serverMessages.filter(
          (m) => !existingIds.has(m.id)
        );
        return [...newServerMessages, ...prev];
      });
      setHasInitializedFromConversation(true);
    }
  }, [conversation, hasInitializedFromConversation]);

  // Add message function (used by ref)
  const addMessage = useCallback(
    (message: Omit<Message, 'id' | 'timestamp'>, shouldAnimate = false) => {
      const newMessage: Message = {
        ...message,
        id: uuidv4(),
        timestamp: new Date(),
      };
      if (!newMessage.isUser && shouldAnimate) {
        setAnimateLastAssistant(true);
      }
      setMessages((prev) => [...prev, newMessage]);
    },
    []
  );

  // Flush pending assistant message
  const flushPendingAssistantMessage = useCallback(() => {
    if (pendingAssistantMessage && chatRef.current) {
      chatRef.current.addMessage(
        {
          text: pendingAssistantMessage,
          isUser: false,
        },
        true
      );
      setPendingAssistantMessage(null);
    }
  }, [pendingAssistantMessage]);

  // Handle sending message
  const handleSendMessage = useCallback(
    async (messageText: string, files?: File[]) => {
      if (!messageText.trim()) return;
      setIsLoading(true);
      // Add user message immediately with files if present
      const userMessage: Message = {
        id: uuidv4(),
        text: messageText,
        isUser: true,
        timestamp: new Date(),
        attachments:
          files && files.length > 0
            ? files.map((f) => ({ name: f.name }))
            : undefined,
      };
      setMessages((prev) => {
        const newMessages = [...prev, userMessage];
        setTimeout(() => {
          if (chatRef.current) {
            const scrollContainer =
              document.querySelector('[role="log"]')?.parentElement;
            if (scrollContainer) {
              scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }
          }
        }, 10);
        return newMessages;
      });
      setPendingAssistantMessage('...'); // Show pending assistant message
      try {
        const chatResult =
          files && files.length > 0
            ? await uploadAiChatWithFiles({
                prompt: messageText,
                files,
                newConversation: isFirstMessage,
              })
            : await aiChatMutation({
                variables: {
                  input: {
                    prompt: messageText,
                    newConversation: isFirstMessage,
                  },
                },
              });

        // Unified error detection
        let unsupportedFileError = false;
        const responseText =
          chatResult?.data?.aiChat?.response ??
          chatResult?.data?.data?.aiChat?.response;
        const errorList = chatResult?.errors;
        if (Array.isArray(errorList)) {
          for (const err of errorList) {
            const code = err?.extensions?.code;
            const msg = String(err?.message || '');
            const extMsg = String(err?.extensions?.message || '');
            const errorString = JSON.stringify(err);
            const isUnsupportedFile =
              msg.toLowerCase().includes('unsupported_file') ||
              extMsg.toLowerCase().includes('unsupported_file') ||
              errorString.toLowerCase().includes('unsupported_file');
            if (code === 'DOWNSTREAM_SERVICE_ERROR' && isUnsupportedFile) {
              unsupportedFileError = true;
              break;
            }
          }
        }

        // If no response and no unsupported_file error, check the whole chatResult for unsupported_file
        if (!responseText && !unsupportedFileError) {
          const chatResultString = JSON.stringify(chatResult);
          if (chatResultString.toLowerCase().includes('unsupported_file')) {
            unsupportedFileError = true;
          }
        }

        setPendingAssistantMessage(null);
        if (unsupportedFileError) {
          setMessages((prev) => [
            ...prev,
            {
              id: uuidv4(),
              text: ERROR_MESSAGES.unsupportedFile,
              isUser: false,
              timestamp: new Date(),
            },
          ]);
        } else if (responseText) {
          setMessages((prev) => [
            ...prev,
            {
              id: uuidv4(),
              text: responseText,
              isUser: false,
              timestamp: new Date(),
            },
          ]);
          setAnimateLastAssistant(true); // Trigger typewriter animation
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: uuidv4(),
              text: ERROR_MESSAGES.general,
              isUser: false,
              timestamp: new Date(),
            },
          ]);
        }
        // Refetch progress data after assistant response
        void Promise.allSettled([
          swrMutate('/v2/items'),
          swrMutate('/v2/item_definitions'),
          apolloClient.refetchQueries({ include: [GetUserInfoQuery] }),
        ]);
      } catch (err) {
        setPendingAssistantMessage(null);
        console.error('Failed to send message', err);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorString = String(
          // @ts-expect-error FIXME: TS2339 - strict mode migration
          (err as unknown)?.message || JSON.stringify(err)
        );
        if (errorString.toLowerCase().includes('unsupported_file')) {
          setMessages((prev) => [
            ...prev,
            {
              id: uuidv4(),
              text: ERROR_MESSAGES.unsupportedFile,
              isUser: false,
              timestamp: new Date(),
            },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: uuidv4(),
              text: ERROR_MESSAGES.general,
              isUser: false,
              timestamp: new Date(),
            },
          ]);
        }
      } finally {
        setIsLoading(false);
        setIsFirstMessage(false);
      }
    },
    [aiChatMutation, uploadAiChatWithFiles, isFirstMessage, apolloClient]
  );

  // Chat ref for imperative actions
  const chatRef = useRef<ChatInterfaceRef>(null);

  // Reset animateLastAssistant to false after animation completes
  const handleTypewriterDone = useCallback(() => {
    setAnimateLastAssistant(false);
  }, []);

  return {
    messages,
    isLoading,
    isStartingNewConversation,
    conversation,
    pendingUserMessage,
    pendingAssistantMessage,
    isFirstMessage,
    chatSessionKey,
    animateLastAssistant,
    hasInitializedFromConversation,
    addMessage,
    flushPendingAssistantMessage,
    handleSendMessage,
    chatRef,
    setMessages,
    setConversation,
    setPendingUserMessage,
    setPendingAssistantMessage,
    setIsFirstMessage,
    setChatSessionKey,
    setAnimateLastAssistant,
    setHasInitializedFromConversation,
    handleTypewriterDone,
    startNewConversation,
  };
};
